import { defineField, defineFormConfig } from '~kui/components/KUI/Core/Form/util/form';
import { markRaw, reactive } from 'vue';
import { z } from 'zod';

import ProfilePhoneField from '~/components/Unified/Profile/ProfilePhoneField.vue';
import { useCanVerifyPhone, useIsConsumerProfile } from '~/composables/unified/useProfileIdentity';
import { useLockedNameFields } from '~/composables/useLockedNameFields';
import { useUIVerification } from '~/composables/Verification/useUIVerification';
import { Gender } from '~/graphql/generated/graphql';
import { useConfigStore } from '~/stores/config';

interface UseProfileFormOptions {
  onVerified?: () => void | Promise<void>;
  // Null hides the marketing emails checkbox: the server does not offer the choice to this student.
  marketingOptedIn?: boolean | null;
  // False leaves out the animations checkbox, for a page that offers the setting on its own.
  withAnimations?: boolean;
}

export const useProfileForm = async (options: UseProfileFormOptions = {}) => {
  const { t } = useI18n();
  const { clusterCountryCodeAlpha2 } = useClusterCountryConfig();
  const { user, accountStudentRole } = useAuth();
  const { isPhoneVerified } = useUIVerification();
  const accessibilityStore = useAccessibilityStore();
  const config = useConfigStore();

  const wasPhoneAlreadySet = computed(() => !!accountStudentRole.value?.phoneNumber);
  // Once set, only support can change a birthday.
  const isBirthDateLocked = !!accountStudentRole.value?.birthDate;
  const isConsumerProfile = useIsConsumerProfile().value;
  const canVerifyPhone = useCanVerifyPhone().value;
  const { firstNameFieldOptions, lastNameFieldOptions } = useLockedNameFields(
    () => accountStudentRole.value?.lockedFields,
    { hint: t('unified.account.nameRosterManaged') },
  );
  const firstNameField = firstNameFieldOptions(z.string().max(255, t('general.formFields.maxLength', { max: 255 })).min(1, t('general.formFields.firstName.required')));
  const lastNameField = lastNameFieldOptions(z.string().max(255, t('general.formFields.maxLength', { max: 255 })).min(1, t('general.formFields.lastName.required')));

  const stateOptions = computed(() =>
    (config.cluster?.country?.states ?? [])
      .map(state => ({ label: state.name, value: state.id })),
  );

  const genderOptions = [
    { label: t('general.formFields.gender.options.male'), value: Gender.Male },
    { label: t('general.formFields.gender.options.female'), value: Gender.Female },
    { label: t('general.formFields.gender.options.nonBinary'), value: Gender.NonBinary },
    { label: t('general.formFields.gender.options.preferNotToSay'), value: Gender.PreferNotToSay },
  ];

  const formConfig = defineFormConfig({
    firstName: defineField({
      type: 'text',
      options: {
        label: t('general.formFields.firstName.label'),
        placeholder: t('general.formFields.firstName.placeholder'),
        required: !firstNameField.disabled,
        dataTestId: 'first-name-input',
        disabled: firstNameField.disabled,
      },
      hint: firstNameField.hint,
      initialValue: accountStudentRole.value?.firstName || '',
      schema: firstNameField.schema,
    }),
    lastName: defineField({
      type: 'text',
      options: {
        label: t('general.formFields.lastName.label'),
        placeholder: t('general.formFields.lastName.placeholder'),
        required: !lastNameField.disabled,
        dataTestId: 'last-name-input',
        disabled: lastNameField.disabled,
      },
      hint: lastNameField.hint,
      initialValue: accountStudentRole.value?.lastName || '',
      schema: lastNameField.schema,
    }),
    email: defineField({
      type: 'text',
      options: {
        label: t('general.formFields.email.label'),
        disabled: true,
        dataTestId: 'email-input',
      },
      initialValue: user.value?.primaryEmail || '',
      schema: z.string(),
    }),
    ...(canVerifyPhone && {
      phoneNumber: {
        initialValue: accountStudentRole.value?.phoneNumber ?? '',
        component: markRaw(ProfilePhoneField),
        props: reactive({
          label: t('general.formFields.phoneNumber.label'),
          get disabled() { return wasPhoneAlreadySet.value || isPhoneVerified.value; },
          baseCountry: clusterCountryCodeAlpha2.value,
          version: 'v2' as const,
          onVerified: options.onVerified,
        }),
        schema: z.string(),
        validationMessage: t('general.formFields.phoneNumber.invalid'),
      },
    }),
    stateId: defineField({
      type: 'select',
      options: {
        label: t('general.formFields.state.label'),
        placeholder: t('general.formFields.state.placeholder'),
        options: stateOptions.value,
        required: true,
        dataTestId: 'state-select',
        version: 'v2',
      },
      initialValue: accountStudentRole.value?.state?.id || '',
      schema: z.string().min(1, t('general.formFields.state.required')),
    }),
    gender: defineField({
      type: 'select',
      options: {
        label: t('general.formFields.gender.label'),
        placeholder: t('general.formFields.gender.placeholder'),
        options: genderOptions,
        required: true,
        dataTestId: 'gender-select',
        version: 'v2',
      },
      initialValue: accountStudentRole.value?.gender ?? '',
      schema: z.enum(Gender, t('general.formFields.gender.required')),
    }),
    ...(isConsumerProfile && {
      birthDate: defineField({
        type: 'date',
        options: {
          dateFormat: t('general.datePicker.formatYmd'),
          label: t('general.formFields.birthDate.label'),
          required: true,
          dataTestId: 'birth-date-input',
          version: 'v2',
          hideChildLabel: true,
          disabled: isBirthDateLocked,
        },
        hint: isBirthDateLocked ? t('general.formFields.birthDate.locked') : undefined,
        initialValue: accountStudentRole.value?.birthDate || '',
        schema: z.coerce.date(t('general.formFields.birthDate.required')),
      }),
    }),
    ...(options.marketingOptedIn != null && {
      marketingEmails: defineField({
        type: 'checkbox',
        options: {
          label: t('unified.onboarding.marketingOptIn'),
          dataTestId: 'marketing-emails-checkbox',
        },
        initialValue: options.marketingOptedIn,
        schema: z.boolean(),
      }),
    }),
    ...(options.withAnimations !== false && {
      animationsEnabled: defineField({
        type: 'checkbox',
        options: {
          label: t('unified.allowAnimation'),
        },
        initialValue: accessibilityStore.animationsEnabled,
        schema: z.boolean(),
      }),
    }),

  }, t('general.formFields.save.label'));

  return { formConfig, wasPhoneAlreadySet, isPhoneVerified };
};
